/* eslint-disable max-len */
// istanbul ignore file
import { BookableServiceV2 } from './oas';
import { Property } from './property';

export enum AccessIssueEntryMethodLabel {
    Appointment = 'APPOINTMENT',
    EnterWithKeyAsEn = 'ENTER_WITH_KEY_AS_EN',
    EnterWithKeyByTenantRequest = 'ENTER_WITH_KEY_BY_TENANT_REQUEST',
    EnterWithKeyVacant = 'ENTER_WITH_KEY_VACANT'
}

export enum AccessIssueSolutionLabel {
    AgencyEmail = 'AGENCY_EMAIL',
    VerifyGoodKey = 'VERIFY_GOOD_KEY',
    VerifyNoOhsIssues = 'VERIFY_NO_OHS_ISSUES',
    VerifyNoDangerousAnimal = 'VERIFY_NO_DANGEROUS_ANIMAL',
    AllowAllFormsOfEntry = 'ALLOW_ALL_FORMS_OF_ENTRY',
    SpeakWithTenant = 'SPEAK_WITH_TENANT',
    ManuallyBookInJobForTenant = 'MANUALLY_BOOK_IN_JOB_FOR_TENANT',
    AddTenantDetails = 'ADD_TENANT_DETAILS',
    UpdateTenantDetails = 'UPDATE_TENANT_DETAILS',
    ConfirmTenantDetails = 'CONFIRM_TENANT_DETAILS',
    ConfirmVacantTenantDetails = 'CONFIRM_PROPERTY_VACANT',
    VerifyUtilitiesConnected = 'VERIFY_UTILITIES_CONNECTED',
    ProvideInstructionsToBypassHomeSecurity = 'PROVIDE_INSTRUCTIONS_TO_BYPASS_HOME_SECURITY',
    ProvideAccessInstructions = 'PROVIDE_ACCESS_INSTRUCTIONS',
    VerifyAllAppliancesAccessible = 'VERIFY_ALL_APPLIANCES_ACCESSIBLE',
    AgencyEmailToOwnersCorpAccessInstructions = 'AGENCY_EMAIL_TO_OWNERS_CORP_ACCESS_INSTRUCTIONS',
    VerifyOccupantsHome = 'VERIFY_OCCUPANTS_HOME',
    AddEmail = 'ADD_EMAIL',
    AddAdditionalPointOfContact = 'ADD_ADDITIONAL_POINT_OF_CONTACT',
    AllowEntryAppt = 'ALLOW_ENTRY_APPT',
    AllowEntryEnInternal = 'ALLOW_ENTRY_EN_INTERNAL',
    AllowEntryTRKP = 'ALLOW_ENTRY_TRKP',
    AllowEntryVacant = 'ALLOW_ENTRY_VACANT',
    MakeTenantDetailsAccurate = 'MAKE_TENANT_DETAILS_ACCURATE',
    VerifyElecConnected = 'VERIFY_ELEC_CONNECTED',
    VerifyGasConnected = 'VERIFY_GAS_CONNECTED',
    VerifyKeyOnsite = 'VERIFY_KEY_ONSITE'
}

export enum AccessIssueSolutionGroup {
    CustomModal = 'CUSTOM_MODAL',
    NotifyDI = 'NOTIFY_DI',
    NotifyDIWithNotes = 'NOTIFY_DI_WITH_NOTES',
    NotifyDIWithDate = 'NOTIFY_DI_WITH_DATE'
}

/**
 * You shouldn't be using these unless you really really really must
 *
 * The DS API will provide you with the correct names for all of these problems
 * to display to users
 */
export enum AccessIssueProblemLabel {
    AppointmentNotAllowed = 'APPOINTMENT_NOT_ALLOWED',
    NoValidContactDetails = 'NO_VALID_CONTACT_DETAILS',
    TenantRefusedEntry = 'TENANT_REFUSED_ENTRY',
    TenantRefusedBooking = 'TENANT_REFUSED_BOOKING',
    TenantRequiresDifferentServicingHours = 'TENANT_REQUIRES_DIFFERENT_SERVICING_HOURS',
    TenantIsNotMakingBooking = 'TENANT_IS_NOT_MAKING_BOOKING',
    TenantCannotSpeakEnglish = 'TENANT_CANNOT_SPEAK_ENGLISH',
    ExistingContactDetailsIncorrect = 'EXISTING_CONTACT_DETAILS_INCORRECT',
    OccupantsNotHome = 'OCCUPANTS_NOT_HOME',
    EnNotAllowed = 'EN_NOT_ALLOWED',
    NoEmailAndHardcopyNotAllowedForEn = 'NO_EMAIL_AND_HARDCOPY_NOT_ALLOWED_FOR_EN',
    TenantHasNotRequestedKeyPickup = 'TENANT_HAS_NOT_REQUESTED_KEY_PICKUP',
    TenantRequestedEnNotAllowed = 'TENANT_REQUESTED_EN_NOT_ALLOWED',
    IsNotVacant = 'IS_NOT_VACANT',
    IsVacant = 'IS_VACANT',
    VacantEntryNotAllowed = 'VACANT_ENTRY_NOT_ALLOWED',
    NoKey = 'NO_KEY',
    BadKey = 'BAD_KEY',
    KeyDistanceTooFar = 'KEY_DISTANCE_TOO_FAR',
    PowerNotConnected = 'POWER_NOT_CONNECTED',
    GasNotConnected = 'GAS_NOT_CONNECTED',
    UnrestrainedAnimal = 'UNRESTRAINED_ANIMAL',
    HomeSecurityBlockingEntry = 'HOME_SECURITY_BLOCKING_ENTRY',
    HomeSecurityBlockingEntryWithoutTenant = 'HOME_SECURITY_BLOCKING_ENTRY_WITHOUT_TENANT',
    KeyPickupsStopped = 'KEY_PICKUPS_STOPPED',
    NoAdult = 'NO_ADULT',
    ExcessiveTenantCancellations = 'EXCESSIVE_TENANT_CANCELLATIONS',
    OhsIssues = 'OHS_ISSUES',
    FipCannotBeAccessed = 'FIP_CANNOT_BE_ACCESSSED',
    AwaitingTenantDetails = 'AWAITING_TENANT_DETAILS',
    ComplexProblem = 'COMPLEX_PROBLEM',
    CannotFindOrAccessProperty = 'CANNOT_FIND_OR_ACCESS_PROPERTY',
    InvalidLatLng = 'INVALID_LAT_LNG'
}

export enum AccessIssueStage {
    StageNotActive = 'STAGE_0',
    StageBlockedAccessIssue = 'STAGE_1',
    StageUnresolvedAccessIssue = 'STAGE_2',
    StageTouchPoint = 'STAGE_2.5',
    StageSuspensionWarning = 'STAGE_3',
    StageSuspension = 'STAGE_4'
}

export type ActiveProblems = {
    problem_label: AccessIssueProblemLabel[];
    problem_start_datetime: string[];
    notes: (string | null)[];
    not_solvable_by_agency: boolean[];
    user_id_responsible_for_flagging_problem: number[];
};

type PropertyCaseDetail = {
    property_case_details: {
        property_id: number;
        case_ids: number[];
        agency_id: number;
        property_manager_id: number;
        service_ids: number[];
        datetime_case_raised: string;
        portal_suppression_level: number;
        pause_suspension_progress: boolean;
        pause_suspension_progress_override: boolean;
        pause_suspension_progress_temporary_override: boolean | null;
        datetime_entered_suspension_level: string;
        suspension_progress: AccessIssueStage;
        oas_bookable_services: BookableServiceV2[] | null;
    };
    active_problems: ActiveProblems;
    blocked_entry_methods: {
        entry_method_label: AccessIssueEntryMethodLabel[];
        problem_label: AccessIssueProblemLabel[];
    };
    suggested_actions: {
        solution_label: AccessIssueSolutionLabel[];
        score: number[];
    };
};

export type AccessIssuePossibleSolution = {
    solution_public_name: string;
    solution_label: AccessIssueSolutionLabel;
    solution_group: AccessIssueSolutionGroup;
    number_of_attempts: number;
    description_integrated: string;
    description_non_integrated: string;
    confirmation_button_text_integrated: string;
    confirmation_button_text_non_integrated: string;
};

export type AccessIssuePossibleProblem = {
    problem_public_name: string;
    problem_description: string;
    problem_set_id: number | null;
    public_opposite_name: string;
    problem_label: AccessIssueProblemLabel;
    party_responsible: string;
    manual_intervention_possible: boolean;
    service_ids_affected: number[];
    service_completion_clears_problem: boolean;
    manual_expiration_entry_allowed: boolean;
    entry_method_labels: AccessIssueEntryMethodLabel[];
    solution_labels: AccessIssueSolutionLabel[];
};

export type AccessIssueActiveProblem = {
    problem: AccessIssuePossibleProblem;
    problem_start_datetime: ActiveProblems['problem_start_datetime'][number];
    notes: ActiveProblems['notes'][0];
    not_solvable_by_agency: ActiveProblems['not_solvable_by_agency'][number];
    user_id_responsible_for_flagging_problem: ActiveProblems['user_id_responsible_for_flagging_problem'][number];
    blocked_entry_methods: AccessIssueEntryMethodLabel[];
};

export type AccessIssueSuggestedAction = {
    solution: AccessIssuePossibleSolution;
    score: PropertyCaseDetail['suggested_actions']['score'][number];
};

export type AccessIssueProblemSet = {
    problem_set_id: number;
    suggested_actions: AccessIssueSuggestedAction[];
    problems: AccessIssueActiveProblem[];
    blocked_entry_methods: AccessIssueEntryMethodLabel[];
    public_name: string;
    public_descriptions: string[];
};

export type AccessIssueProblemCaseDetails = PropertyCaseDetail['property_case_details'];

export type AccessIssueProblemCase = {
    problem_case_details: PropertyCaseDetail['property_case_details'];
    blocked_entry_method_keys: AccessIssueEntryMethodLabel[];
    all_entry_methods_disqualified: boolean;
    blocked_entry_methods: {
        [key: string]: {
            blocked_entry_method: AccessIssueEntryMethodLabel;
            /**
             * Groups of problems that can be resolved together
             * with a set of suggested actions
             */
            problem_sets: AccessIssueProblemSet[];
            /**
             * Whether this entry method is blocked by agency/property preferences
             * Internally this is determined by having specific problems within the problem sets
             */
            is_blocked_by_preferences: boolean;
        };
    };
};

type PropertyIdString = string;

export type PropertyProblemCase = { [key: PropertyIdString]: PropertyCaseDetail };

export type AgencyProblemCase = { [key: PropertyIdString]: PropertyCaseDetail };

export type PropertyRefreshProblemCase = null;

export type AccessIssueApiResponse<T> = {
    success: boolean;
    data: T | null;
    error: string | null;
};

export enum AccessIssueFacets {
    Overdue = 'OVERDUE',
    NotOverdue = 'NOT_OVERDUE',
    PriorityBooking = 'PRIORITY_BOOKING',
    Vacant = 'VACANT',
    OnHold = 'ON_HOLD',
    UpdateRequired = 'UPDATE_REQUIRED',
    Suspended = 'SUSPENDED',
    SuspensionWarning = 'SUSPENSION_WARNING'
}

type AgencyEntryMethodPreferenceData = {
    id: number;
    agencyId: number;
    createdById: number;
    createdAt: string;
    modifiedById: number | null;
    modifiedAt: string | null;
    occupantAppointmentPreference: number;
    occupantSingleKeyPreference: number;
    internalEnPreference: number;
    vacantEntryPreference: number;
    appointmentPmPreference: number;
    appointmentThirdPartyPreference: number;
    agencyEnPreference: number;
};

export type CompliancePropertyData = Property & {
    agencyEntryMethod: {
        AgencyEntryMethodPreference: AgencyEntryMethodPreferenceData;
    };
};

export type ProblemData = {
    problemLabel: string;
    createdAt: string;
    publicName: string;
    isUrgent: boolean;
    isResolved: boolean;
    isContactDiRequired: boolean;
    isVacant: boolean;
};

export type AccessIssuesData = {
    suspensionLevel: string;
    problems: ProblemData[];
};

export type PropertyAccessIssuesData = CompliancePropertyData & {
    accessIssues: AccessIssuesData;
    tags: string[];
};
